import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, errorText } from '../../api.ts'
import type { AdminCompany, AppRef, Company } from '../../types.ts'
import { AppList, AppMark, Badge, byKind, itemName, Field, Loading, Modal, Notice } from '../../ui.tsx'
import { Button } from '../../components/Button.tsx'
import { Plus } from 'lucide-react'

interface CompaniesResponse {
  companies: AdminCompany[]
  allApps: AppRef[]
}

export const statusTone = (status: Company['status']) =>
  status === 'active' ? 'ok' : status === 'suspended' ? 'warn' : 'bad'

/** Every company, for Icefield administrators (PLAN.md 1.6). */
export function Companies() {
  const list = useQuery({ queryKey: ['admin', 'companies'], queryFn: () => api.get<CompaniesResponse>('/admin/companies') })
  const [creating, setCreating] = useState(false)

  return (
    <section>
      <div className="section-head">
        <h1>Companies</h1>
        <Button icon={Plus} onClick={() => setCreating(true)}>New company</Button>
      </div>
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <Notice>{errorText(list.error)}</Notice>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th>Status</th>
                <th className="center">Users</th>
                <th>Apps</th>
              </tr>
            </thead>
            <tbody>
              {list.data.companies.map((c) => (
                <tr key={c.id}>
                  <td className="nowrap">
                    <Link to={`/admin/companies/${c.id}`}><strong>{c.name}</strong></Link>
                    <div className="muted small">{c.slug}</div>
                  </td>
                  <td><Badge tone={statusTone(c.status)}>{c.status}</Badge></td>
                  <td className="center">{c.people}</td>
                  <td className="small">
                    <AppList apps={c.apps.map((a) => list.data.allApps.find((x) => x.appId === a) ?? { appId: a, name: a })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal title="New company" open={creating} onClose={() => setCreating(false)}>
        {list.data && <NewCompany allApps={list.data.allApps} onDone={() => setCreating(false)} />}
      </Modal>
    </section>
  )
}

const slugify = (name: string) =>
  name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 63)

function NewCompany({ allApps, onDone }: { allApps: AppRef[]; onDone: () => void }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [domains, setDomains] = useState('')
  const [entraTenantId, setEntraTenantId] = useState('')
  const [allowPersonalMs, setAllowPersonalMs] = useState(false)
  const [apps, setApps] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const company = await api.post<Company>('/admin/companies', {
        name,
        slug,
        domains: domains.split(/[\s,]+/).filter(Boolean),
        signIn: { entraTenantId: entraTenantId.trim() || null, allowPersonalMs },
        apps,
      })
      await queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] })
      onDone()
      navigate(`/admin/companies/${company.id}`)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <Notice>{error}</Notice>
      <Field label="Name">
        <input
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value)
            if (!slugTouched) setSlug(slugify(e.target.value))
          }}
        />
      </Field>
      <Field label="Slug" hint="Lowercase letters, numbers and hyphens. It can't be changed later.">
        <input
          required
          value={slug}
          pattern="[a-z0-9]+(-[a-z0-9]+)*"
          onChange={(e) => {
            setSlugTouched(true)
            setSlug(e.target.value)
          }}
        />
      </Field>
      <Field label="Email domains" hint="One per line.">
        <textarea rows={2} value={domains} onChange={(e) => setDomains(e.target.value)} />
      </Field>
      <Field label="Entra tenant id" hint="Optional. Recorded for the company; sign-in doesn't enforce it yet.">
        <input value={entraTenantId} onChange={(e) => setEntraTenantId(e.target.value)} />
      </Field>
      <label className="check">
        <input type="checkbox" checked={allowPersonalMs} onChange={(e) => setAllowPersonalMs(e.target.checked)} />
        Allow personal Microsoft accounts
      </label>
      {byKind(allApps).map((group) => (
        <fieldset key={group.kind}>
          <legend>{group.label}</legend>
          {group.items.map((a) => (
            <label key={a.appId} className="check">
              <input
                type="checkbox"
                checked={apps.includes(a.appId)}
                onChange={(e) => setApps(e.target.checked ? [...apps, a.appId] : apps.filter((x) => x !== a.appId))}
              />
              <AppMark appId={a.appId} name={a.name} kind={a.kind} size={24} />
              {itemName(a)}
            </label>
          ))}
        </fieldset>
      ))}
      <p className="muted small">Next, add the company's first administrator on its Users tab.</p>
      <div className="form-actions">
        <Button type="submit" disabled={busy}>Create</Button>
      </div>
    </form>
  )
}
